import { supabase } from '../../providers/supabase/client'

export interface CruiseShip {
    id: string
    name: string
    cruise_line: string
    destinations?: string[] | null
    main_image_url?: string | null
    gallery_urls?: string[] | null
    capacity?: number | null
    crew?: number | null
    year_built?: number | null
    tonnage?: string | null
    decks?: number | null
    amenities?: string[] | null
    description?: string | null
    active?: boolean
    created_at?: string
    updated_at?: string
}

// ─── CRUD ───────────────────────────────────────────────────

export const fetchCruiseShips = async () => {
    return await supabase
        .from('cruise_ships')
        .select('*')
        .order('name', { ascending: true })
}

export const createCruiseShip = async (ship: CruiseShip) => {
    return await supabase
        .from('cruise_ships')
        .insert([ship])
        .select()
}

export const updateCruiseShip = async (id: string, ship: Partial<CruiseShip>) => {
    return await supabase
        .from('cruise_ships')
        .update(ship)
        .eq('id', id)
        .select()
}

export const deleteCruiseShip = async (id: string) => {
    return await supabase
        .from('cruise_ships')
        .delete()
        .eq('id', id)
}

// ─── STORAGE ────────────────────────────────────────────────

const BUCKET = 'cruise-ship-images'

/**
 * Upload a cruise ship image to a deterministic slot.
 * Slot examples: 'main', 'gallery_0', 'gallery_1', etc.
 * Uploads under ships/${shipId}/${slot}.
 * Uses upsert so re-uploads overwrite the same object.
 */
export const uploadCruiseShipImage = async (
    shipId: string,
    file: File | Blob,
    slot: string,
): Promise<string | null> => {
    let fileExt = 'webp'
    if ((file as any)?.name && typeof (file as any).name === 'string' && (file as any).name.includes('.')) {
        fileExt = (file as any).name.split('.').pop() || 'webp'
    } else if (file.type && file.type.includes('/')) {
        const sub = file.type.split('/')[1]
        fileExt = sub === 'jpeg' ? 'jpg' : (sub || 'webp')
    }

    const contentType = file.type || (fileExt === 'webp' ? 'image/webp' : `image/${fileExt}`)
    const path = `ships/${shipId}/${slot}.${fileExt}`

    const { error } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
            upsert: true,
            contentType,
            cacheControl: '0',
        })

    if (error) {
        console.error(`Error uploading ship image [${slot}]:`, error)
        return null
    }

    const { data: urlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(path)

    return urlData?.publicUrl ? `${urlData.publicUrl}?t=${Date.now()}` : null
}

/**
 * Delete a specific cruise ship image from storage by slot.
 */
export const deleteCruiseShipImage = async (
    shipId: string,
    slot: string,
): Promise<boolean> => {
    const extensions = ['webp', 'jpg', 'jpeg', 'png', 'gif']
    const paths = extensions.map(ext => `ships/${shipId}/${slot}.${ext}`)

    const { error } = await supabase.storage
        .from(BUCKET)
        .remove(paths)

    if (error) {
        console.error(`Error deleting ship image [${slot}]:`, error)
        return false
    }
    return true
}
